import { brand } from './brand';
/**
 * @module i18n
 * @description i18next 初始化配置
 * 雙層覆蓋：locale JSON → DB 自訂術語（App 啟動時從 /api/i18n/custom-terms 載入）
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import { timeoutSignal } from './timeoutSignal';

const SUPPORTED_LANGUAGES = ['zh-TW', 'zh-CN', 'en'] as const;
const DEFAULT_LANGUAGE = 'zh-TW';
const NAMESPACES = [
  'common',
  'member',
  'accounting',
  'tournament',
  'auth',
  'display',
  'admin',
  'venue',
] as const;

i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    lng:
      (() => {
        try {
          return localStorage.getItem('language');
        } catch {
          return null;
        }
      })() ?? DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    ns: [...NAMESPACES],
    defaultNS: 'common',

    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    interpolation: {
      escapeValue: false,
      defaultVariables: { brandName: brand.name },
    },

    // 開發環境顯示 missing key
    debug: (import.meta as unknown as { env: { DEV: boolean } }).env.DEV,
  });

/**
 * 從後端 API 載入 DB 層自訂術語，合併覆蓋 i18n 資源
 */
export async function loadCustomTermsOverride(apiBaseUrl = '/api'): Promise<void> {
  try {
    const res = await fetch(`${apiBaseUrl}/i18n/custom-terms`, {
      signal: timeoutSignal(10000),
    });
    if (!res.ok) return;

    const json: {
      success: boolean;
      data: Array<{
        namespace: string;
        termKey: string;
        zhTW: string;
        zhCN?: string | null;
        en?: string | null;
      }>;
    } = await res.json();
    const terms = json.data ?? [];

    // 依 namespace 分組
    const overrideMap: Record<string, Record<string, Record<string, string>>> = {};

    for (const term of terms) {
      if (!overrideMap['zh-TW']) overrideMap['zh-TW'] = {};
      if (!overrideMap['zh-CN']) overrideMap['zh-CN'] = {};
      if (!overrideMap['en']) overrideMap['en'] = {};

      const zhTWNs = overrideMap['zh-TW'] as Record<string, Record<string, string>>;
      zhTWNs[term.namespace] ??= {};
      (zhTWNs[term.namespace] as Record<string, string>)[term.termKey] = term.zhTW;

      if (term.zhCN) {
        const zhCNNs = overrideMap['zh-CN'] as Record<string, Record<string, string>>;
        zhCNNs[term.namespace] ??= {};
        (zhCNNs[term.namespace] as Record<string, string>)[term.termKey] = term.zhCN;
      }

      if (term.en) {
        const enNs = overrideMap['en'] as Record<string, Record<string, string>>;
        enNs[term.namespace] ??= {};
        (enNs[term.namespace] as Record<string, string>)[term.termKey] = term.en;
      }
    }

    // 注入覆蓋資源（addResourceBundle 合併模式）
    for (const [lang, nsMap] of Object.entries(overrideMap)) {
      for (const [ns, resources] of Object.entries(nsMap)) {
        i18n.addResourceBundle(lang, ns, resources, true, true);
      }
    }
  } catch (err) {
    console.warn('[i18n] Failed to load custom terms:', err);
  }
}

export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, NAMESPACES };
export default i18n;
